import { z } from 'zod';
import type { ActiveCall } from './calls.js';
import type { Channel } from './channels.js';
import type { Meeting } from './meetings.js';
import type { Message } from './messages.js';
import type { Notification } from './notifications.js';
import type { Task } from './tasks.js';
import type { TeamMember } from './teams.js';
import type { UserSummary } from './users.js';

/**
 * Realtime runs over the Pusher protocol (EventSocket in production). The server publishes
 * events through the provider's REST API; clients subscribe to private channels that our API
 * authorises. Channel names are shared here so client and server can never drift apart.
 */
export const realtimeChannels = {
  /** Per-user events: notifications, new private channels/DMs. */
  user: (userId: string) => `private-user.${userId}`,
  /** Team-wide events: public tasks, new public channels, new members. */
  team: (teamId: string) => `private-team.${teamId}`,
  /** Conversation events: messages, typing, tasks created from private conversations. */
  channel: (channelId: string) => `private-channel.${channelId}`,
  /** Presence channel used to know who is online in a team. */
  presence: (teamId: string) => `presence-team.${teamId}`,
};

export type RealtimeChannelKind = keyof typeof realtimeChannels;

const prefixes: Record<string, RealtimeChannelKind> = {
  'private-user': 'user',
  'private-team': 'team',
  'private-channel': 'channel',
  'presence-team': 'presence',
};

/** Parses a channel name back into its kind and id (null if not one of ours). */
export function parseRealtimeChannel(
  name: string,
): { kind: RealtimeChannelKind; id: string } | null {
  const match = /^(private-user|private-team|private-channel|presence-team)\.([0-9a-f-]{36})$/.exec(
    name,
  );
  const [, prefix, id] = match ?? [];
  const kind = prefix ? prefixes[prefix] : undefined;
  return kind && id ? { kind, id } : null;
}

/** Event name -> payload, for every event the server publishes. */
export interface RealtimeEvents {
  'message:created': Message;
  'message:updated': Message;
  'message:deleted': { id: string; channelId: string };
  'channel:created': Channel;
  'task:created': Task;
  'task:updated': Task;
  'task:deleted': { id: string; teamId: string };
  /** Many tasks changed at once (e.g. a sprint was completed): clients refetch their list. */
  'tasks:changed': { teamId: string };
  /** The board columns of the team changed (created, renamed, reordered or deleted). */
  'columns:changed': { teamId: string };
  /** Sprints of the team were created, edited, started, completed or deleted. */
  'sprints:changed': { teamId: string };
  /** The team itself changed (name, task key). */
  'team:updated': { teamId: string };
  /** The owner deleted the team. */
  'team:deleted': { teamId: string };
  'member:joined': { teamId: string; member: TeamMember };
  'member:updated': { teamId: string; member: TeamMember };
  'member:removed': { teamId: string; userId: string };
  /** Custom roles of the team changed (created, edited or deleted). */
  'roles:changed': { teamId: string };
  /**
   * Channels or permissions of the team changed (renamed, deleted, overrides, roles…): what each
   * person sees depends on their permissions, so clients refetch their own list.
   */
  'channels:changed': { teamId: string };
  typing: { channelId: string; userId: string; displayName: string };
  /** A meeting was scheduled, edited, answered or cancelled (team channel). */
  'meeting:updated': Meeting;
  /** Who is in the conversation's call (published on the conversation channel). */
  'call:updated': ActiveCall;
  /** Someone in the call started or stopped recording it for an AI summary. */
  'call:recording': { channelId: string; userId: string; displayName: string; on: boolean };
  /** Someone started a call in the conversation: others get a "join" prompt. */
  'call:started': { channelId: string; teamId: string; startedBy: UserSummary };
  'notification:created': Notification;
  /** Sessions closed from another device (user channel): those devices sign out right away. */
  'session:revoked': { sessionIds: string[] };
}

export type RealtimeEventName = keyof RealtimeEvents;

/** Public connection settings the client needs (never includes the secret). */
export type RealtimeConfig =
  | { enabled: false }
  | { enabled: true; key: string; wsHost: string; wsPort: number; forceTLS: boolean };

/** Pusher socket ids look like `1234.5678`. */
const socketIdSchema = z.string().regex(/^\d+\.\d+$/, 'Invalid socket id');

export const realtimeAuthSchema = z.object({
  socketId: socketIdSchema,
  channelName: z.string().max(200),
});
export type RealtimeAuthInput = z.infer<typeof realtimeAuthSchema>;

export const typingSchema = z.object({
  /** Sender's socket id, so the event is not echoed back to them. */
  socketId: socketIdSchema.optional(),
});
export type TypingInput = z.infer<typeof typingSchema>;

export interface RealtimeAuthResponse {
  auth: string;
  channel_data?: string;
}

/** User info attached to presence channel members. */
export interface PresenceUserInfo {
  displayName: string;
}
